import { FC, useMemo, useState } from 'react'

import { Box, Title } from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type PersonalInformationFormData, benefitEnrollmentField } from '../types'
import { personalInformationStepSchema } from '../validation'

interface Props {
  data: PersonalInformationFormData
  identifierTypes: Array<{ label: string; value: string }>
  onChange: (fieldName: string, value: string) => void
}

const PersonalInformationForm: FC<Props> = ({ data, identifierTypes, onChange }) => {
  // Memoize the schema to avoid recreation
  const schema = useMemo(() => personalInformationStepSchema(), [])

  // Track which fields have been touched/blurred to show validation errors
  const [touchedFields, setTouchedFields] = useState<Set<string>>(() => {
    // Mark as touched any fields that already have a value
    return new Set(
      Object.keys(data)
        .map((field) => {
          const typedField = benefitEnrollmentField`${field}`
          return data[typedField]?.length ?? 0 ? typedField : null
        })
        .filter((field) => field !== null)
    )
  })

  // Helper to mark field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName))
  }

  // Validate the current data
  const validationResult = schema.safeParse(data)

  // Extract field-specific errors from Zod validation, but only show if field was touched
  const getFieldError = (fieldName: string): string | undefined => {
    if (validationResult.success || !touchedFields.has(fieldName)) return undefined

    const fieldError = validationResult.error.errors.find((error) => error.path.includes(fieldName))
    return fieldError?.message
  }

  // Sex options
  const sexOptions = [
    { label: i18n.t('common.select'), value: '' },
    { label: i18n.t('common.male'), value: 'male' },
    { label: i18n.t('common.female'), value: 'female' },
  ]

  return (
    <Box flexDirection="column" gap="s24" width="s400">
      <Box flexDirection="column" gap="s8">
        <Title
          variant="500"
          title={i18n.t('benefits.healthcare.enrollment_wizard.personal_information_step.title')}
          description={i18n.t(
            'benefits.healthcare.enrollment_wizard.personal_information_step.description'
          )}
        />
      </Box>

      <Box flexDirection="column" gap="s16">
        {/* Name and Surname */}
        <Box flexDirection="row" gap="s12">
          <Box style={{ flex: '1' }}>
            <TextField
              label={i18n.t('benefits.healthcare.member_data.name.label')}
              mandatory
              value={data.firstName}
              onChange={(value: string) => onChange('firstName', value)}
              onBlur={() => markFieldTouched('firstName')}
              error={getFieldError('firstName')}
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <TextField
              label={i18n.t('benefits.healthcare.member_data.surname.label')}
              mandatory
              value={data.lastName}
              onChange={(value: string) => onChange('lastName', value)}
              onBlur={() => markFieldTouched('lastName')}
              error={getFieldError('lastName')}
            />
          </Box>
        </Box>

        {/* Sex */}
        <SelectField
          label={i18n.t('benefits.healthcare.member_data.sex.label')}
          mandatory
          value={data.sex}
          options={sexOptions}
          onChange={(value: string) => onChange('sex', value)}
          onBlur={() => markFieldTouched('sex')}
          error={getFieldError('sex')}
        />

        {/* Date of Birth */}
        <DayPicker
          label={i18n.t('benefits.healthcare.member_data.birthday.label')}
          mandatory
          value={data.birthday ? dateBackendToUI(data.birthday) : undefined}
          onChange={(value: string) => onChange('birthday', dateUIToBackend(value))}
          error={getFieldError('birthday')}
        />

        {/* Address */}
        <TextField
          label={i18n.t('benefits.healthcare.member_data.address.label')}
          mandatory
          value={data.addressLine1}
          onChange={(value: string) => onChange('addressLine1', value)}
          onBlur={() => markFieldTouched('addressLine1')}
          error={getFieldError('addressLine1')}
        />

        <TextField
          label={i18n.t('benefits.healthcare.member_data.address_line_2.label')}
          value={data.addressLine2}
          onChange={(value: string) => onChange('addressLine2', value)}
          onBlur={() => markFieldTouched('addressLine2')}
          error={getFieldError('addressLine2')}
        />

        {/* Postal Code and City */}
        <Box flexDirection="row" gap="s12">
          <Box style={{ flex: '1' }}>
            <TextField
              label={i18n.t('benefits.healthcare.member_data.postal_code.label')}
              mandatory
              value={data.postalCode}
              onChange={(value: string) => onChange('postalCode', value)}
              onBlur={() => markFieldTouched('postalCode')}
              error={getFieldError('postalCode')}
            />
          </Box>
          <Box style={{ flex: '1' }}>
            <TextField
              label={i18n.t('benefits.healthcare.member_data.city.label')}
              mandatory
              value={data.city}
              onChange={(value: string) => onChange('city', value)}
              onBlur={() => markFieldTouched('city')}
              error={getFieldError('city')}
            />
          </Box>
        </Box>

        {/* Province */}
        <TextField
          label={i18n.t('benefits.healthcare.member_data.province.label')}
          mandatory
          value={data.province}
          onChange={(value: string) => onChange('province', value)}
          onBlur={() => markFieldTouched('province')}
          error={getFieldError('province')}
        />

        <Box flexDirection="row" gap="s12">
          {/* Tax ID */}
          <Box style={{ flex: '1' }}>
            <TextField
              label={i18n.t('benefits.healthcare.member_data.identifier_number.label')}
              mandatory
              value={data.taxIdNumber}
              onChange={(value: string) => onChange('taxIdNumber', value)}
              onBlur={() => markFieldTouched('taxIdNumber')}
              error={getFieldError('taxIdNumber')}
            />
          </Box>
          {/* Tax ID Type */}
          <Box style={{ flex: '1' }}>
            <SelectField
              label={i18n.t('benefits.healthcare.member_data.identifier_type.label')}
              mandatory
              value={data.taxIdType}
              options={identifierTypes}
              onChange={(value: string) => onChange('taxIdType', value)}
              onBlur={() => markFieldTouched('taxIdType')}
              error={getFieldError('taxIdType')}
            />
          </Box>
        </Box>

        {/* Email */}
        <TextField
          label={i18n.t('common.email')}
          mandatory
          value={data.email}
          onChange={(value: string) => onChange('email', value)}
          onBlur={() => markFieldTouched('email')}
          error={getFieldError('email')}
        />

        {/* Phone Number */}
        <TextField
          label={i18n.t('benefits.healthcare.member_data.phone.label')}
          mandatory
          value={data.phoneNumber}
          onChange={(value: string) => onChange('phoneNumber', value)}
          onBlur={() => markFieldTouched('phoneNumber')}
          error={getFieldError('phoneNumber')}
          placeholder="+34695858423"
        />

        {/* IBAN */}
        <TextField
          label={i18n.t('benefits.healthcare.member_data.iban.label')}
          mandatory
          value={data.iban}
          onChange={(value: string) => onChange('iban', value)}
          placeholder={i18n.t('benefits.healthcare.member_data.iban.placeholder')}
          onBlur={() => markFieldTouched('iban')}
          error={getFieldError('iban')}
        />
      </Box>
    </Box>
  )
}

export default PersonalInformationForm
